import type { QueryClient } from '@tanstack/react-query';
import {
  type FeatureFlagValues,
  LOCAL_FEATURE_FLAG_VALUES,
} from '@his/feature-flags';
import { redirect } from '@tanstack/react-router';
import type { Permission } from '@/types/permission';
import { AUTH_ROUTES } from '@/features/auth/consts/routes';
import { DASHBOARD_ROUTES } from '@/features/dashboard/consts/routes';
import { FEATURE_FLAGS_CONFIG } from '@/lib/env';
import {
  FEATURE_FLAGS_QUERY_KEY,
  getFeatureFlagsQueryOptions,
} from '@/services/use-query-feature-flags';
import { hasPermissions } from '../idb/models/permissions';
import { getCurrentUser } from '../idb/models/users';

type RouterGuardContext = {
  queryClient: QueryClient;
  isBoneyardBuild?: boolean;
};

type RouterGuardOptions = {
  featureFlag?: keyof FeatureFlagValues;
};

export const resolveFeatureFlags = async (
  context: RouterGuardContext,
): Promise<FeatureFlagValues> => {
  if (context.isBoneyardBuild || FEATURE_FLAGS_CONFIG.mode === 'local') {
    return LOCAL_FEATURE_FLAG_VALUES;
  }

  const featureFlagsQueryState = context.queryClient.getQueryState(
    FEATURE_FLAGS_QUERY_KEY,
  );

  if (featureFlagsQueryState?.error) {
    throw featureFlagsQueryState.error;
  }

  return context.queryClient.ensureQueryData(getFeatureFlagsQueryOptions());
};

export const beforeLoadRouterGuard =
  (permissions: Permission[], options?: RouterGuardOptions) =>
  async ({ context }: { context: RouterGuardContext }) => {
    if (context.isBoneyardBuild) {
      return;
    }

    if (options?.featureFlag) {
      const flags = await resolveFeatureFlags(context);

      if (!flags[options.featureFlag]) {
        throw redirect({
          to: DASHBOARD_ROUTES.index,
          ignoreBlocker: true,
        });
      }
    }

    const user = await getCurrentUser({ nonNullable: true });

    if (!(await hasPermissions(user.id, permissions))) {
      throw redirect({
        to: AUTH_ROUTES.unauthorized,
        ignoreBlocker: true,
      });
    }
  };
